import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import axios from 'axios';
import './BillingDetails.css';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

Chart.register(...registerables);

const BillDistribution = () => {
  const [billDistribution, setBillDistribution] = useState({
    cashBills: 0,
    creditBills: 0,
    creditNotes: 0,
    debitNotes: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [chartInstance, setChartInstance] = useState(null);
  const [allCashBills, setAllCashBills] = useState([]);
  const [allCreditBills, setAllCreditBills] = useState([]);
  const [allCreditNotes, setAllCreditNotes] = useState([]);
  const [allDebitNotes, setAllDebitNotes] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`; // YYYY-MM
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const API_BASE_URL = API_CONFIG.BASE_URL;
  
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      return 'mock-token-for-testing';
    }
    
    const lastFirebaseError = localStorage.getItem('firebaseAuthFailed');
    const errorTime = lastFirebaseError ? parseInt(lastFirebaseError) : 0;
    const now = Date.now();
    
    if (now - errorTime < 600000) {
      return 'mock-token-for-testing';
    }
    
    try {
      let token = await currentUser.getIdToken(false);
      if (token) {
        return token;
      }
      return await currentUser.getIdToken(true);
    } catch (error) {
      localStorage.setItem('firebaseAuthFailed', now.toString());
      return 'mock-token-for-testing';
    }
  }, [currentUser]);
  
  const api = useCallback(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  }), [API_BASE_URL]);

  // Fetch all bills once, then compute monthly distribution client-side
  useEffect(() => {
    const fetchAllBills = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      try {
        console.log('🔍 Fetching all bills for monthly distribution...');
          const token = await getAuthToken();

        const client = api();
        const [cashRes, creditRes, notesRes, debitRes] = await Promise.all([
          client.get('/accounts/bill-details/cashBills', { headers: { Authorization: `Bearer ${token}` } }),
          client.get('/accounts/bill-details/creditBills', { headers: { Authorization: `Bearer ${token}` } }),
          client.get('/accounts/bill-details/creditNotes', { headers: { Authorization: `Bearer ${token}` } }),
          client.get('/accounts/bill-details/debitNotes', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const normalize = (resp) => {
          const d = resp?.data;
          if (Array.isArray(d)) return d;
          if (Array.isArray(d?.value)) return d.value;
          if (Array.isArray(d?.data)) return d.data;
          return [];
        };

        const cash = normalize(cashRes);
        const credit = normalize(creditRes);
        const notes = normalize(notesRes);
        const debit = normalize(debitRes);

        setAllCashBills(cash);
        setAllCreditBills(credit);
        setAllCreditNotes(notes);
        setAllDebitNotes(debit);

      } catch (err) {
        setNotificationMessage({ type: 'error', text: `Failed to fetch bills: ${err.message}` });
        setTimeout(() => setNotificationMessage(null), 5000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllBills();
  }, [currentUser, getAuthToken, api]);

  // Compute distribution for selected month
  useEffect(() => {
    const isInSelectedMonth = (dateLike) => {
      if (!dateLike) return false;
      const d = new Date(dateLike);
      if (isNaN(d)) return false;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${yyyy}-${mm}` === selectedMonth;
    };

    const sumAmounts = (items, pickAmount) => {
      return items.reduce((sum, it) => {
        const amount = Number(pickAmount(it));
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
    };

    // Filter by selected month
    const cashMonth = allCashBills.filter(b => isInSelectedMonth(b.date || b.createdAt));
    const creditMonth = allCreditBills.filter(b => isInSelectedMonth(b.date || b.createdAt));
    const notesMonth = allCreditNotes.filter(n => isInSelectedMonth(n.date || n.createdAt));
    const debitMonth = allDebitNotes.filter(n => isInSelectedMonth(n.date || n.createdAt));

    // Compute with robust fallbacks
    const cashTotal = sumAmounts(cashMonth, (b) => b.grandTotal ?? b.totals?.grandTotal ?? b.totalAmount ?? 0);
    const creditTotal = sumAmounts(creditMonth, (b) => b.grandTotal ?? b.totals?.grandTotal ?? b.totalAmount ?? 0);
    const creditNotesTotal = sumAmounts(notesMonth, (n) => n.Total ?? n.totals?.rounded ?? n.totalAmount ?? 0);
    const debitNotesTotal = sumAmounts(debitMonth, (n) => n.totalAmount ?? n.totals?.totalAmount ?? 0);

    setBillDistribution({
      cashBills: cashTotal,
      creditBills: creditTotal,
      creditNotes: creditNotesTotal,
      debitNotes: debitNotesTotal
    });
  }, [allCashBills, allCreditBills, allCreditNotes, allDebitNotes, selectedMonth]);

  // Create pie chart
  useEffect(() => {
    if (!billDistribution || Object.values(billDistribution).every(val => val === 0)) {
      return;
    }

    setChartLoading(true);
    
    const createChart = () => {
      // Try multiple ways to find the canvas
      let ctx = document.getElementById('billDistributionChart');
      
      if (!ctx) {
        // Try querySelector as fallback
        ctx = document.querySelector('#billDistributionChart');
      }
      
      if (!ctx) {
        // Try finding by class or other attributes
        ctx = document.querySelector('canvas[id="billDistributionChart"]');
      }
      
      if (!ctx) {
        // Try to create the canvas element if it doesn't exist
        const chartContainer = document.querySelector('.pie-chart-container');
        if (chartContainer) {
          const newCanvas = document.createElement('canvas');
          newCanvas.id = 'billDistributionChart';
          newCanvas.width = 400;
          newCanvas.height = 400;
          chartContainer.appendChild(newCanvas);
          ctx = newCanvas;
        } else {
          return false;
        }
      }


      // Check if canvas is properly rendered
      if (!ctx.getContext) {
        return false;
      }

      // Destroy existing chart if it exists
      if (chartInstance) {
        chartInstance.destroy();
      }

    const chartData = {
      labels: ['Cash Bills', 'Credit Bills', 'Credit Notes', 'Debit Notes'],
        datasets: [{
          data: [
            billDistribution.cashBills,
            billDistribution.creditBills,
            billDistribution.creditNotes,
            billDistribution.debitNotes
          ],
          backgroundColor: ['#3498db', '#9b59b6', '#e74c3c', '#f1c40f'],
          borderColor: '#fff',
          borderWidth: 2,
        }]
    };

      try {
        const newChartInstance = new Chart(ctx, {
      type: 'pie',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
                position: 'bottom',
                labels: {
                  padding: 20,
                  usePointStyle: true,
                  font: {
                    size: 12
                  }
                }
          },
          tooltip: {
            callbacks: {
                  label: function(context) {
                const label = context.label || '';
                    const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: ₹${value.toLocaleString()} (${percentage}%)`;
                  }
                }
              }
            }
          }
        });

        setChartInstance(newChartInstance);
        setChartLoading(false);
        return true;
      } catch (error) {
        setChartLoading(false);
        return false;
      }
    };

    // Simple chart initialization without retry logic
    const initTimeout = setTimeout(() => {
      createChart();
    }, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(initTimeout);
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [billDistribution, chartInstance]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTotalAmount = () => {
    return billDistribution.cashBills + billDistribution.creditBills + 
           billDistribution.creditNotes + billDistribution.debitNotes;
  };

  return (
    <div className="billing-details">
      <div className="bill-distribution-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Go Back"
          >
            ← Back
          </button>
      <h2>Bill Distribution & Analytics</h2>
        </div>
        <div className="month-filter-section">
          <label htmlFor="month-filter">
            <strong>Month:</strong>
            <input
              type="month"
              id="month-filter"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              aria-label="Select month to filter distribution"
            />
          </label>
        </div>
      </div>

      {notificationMessage && (
        <div className={`message ${notificationMessage.type}`}>
          <p>{notificationMessage.text}</p>
          <button
            type="button"
            onClick={() => setNotificationMessage(null)}
            aria-label="Close message"
          >
            Close
          </button>
        </div>
      )}

      {isLoading && <div className="loading-overlay">Loading...</div>}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card cash-bills">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Cash Bills</h3>
            <p className="amount">{formatCurrency(billDistribution.cashBills)}</p>
          </div>
        </div>

        <div className="summary-card credit-bills">
          <div className="card-icon">💳</div>
          <div className="card-content">
            <h3>Credit Bills</h3>
            <p className="amount">{formatCurrency(billDistribution.creditBills)}</p>
        </div>
      </div>

        <div className="summary-card credit-notes">
          <div className="card-icon">📝</div>
          <div className="card-content">
            <h3>Credit Notes</h3>
            <p className="amount">{formatCurrency(billDistribution.creditNotes)}</p>
            </div>
            </div>
        
        <div className="summary-card debit-notes">
          <div className="card-icon">📄</div>
          <div className="card-content">
            <h3>Debit Notes</h3>
            <p className="amount">{formatCurrency(billDistribution.debitNotes)}</p>
            </div>
          </div>
        </div>

      {/* Total Summary */}
      <div className="total-summary">
        <h3>Total Amount: {formatCurrency(getTotalAmount())}</h3>
      </div>

      {/* Pie Chart */}
      <div className="chart-container">
        <h3>Bill Distribution Chart</h3>
        <div className="pie-chart-container">
          {chartLoading ? (
            <div className="chart-loading">
              <div className="loading-spinner"></div>
              <p>Loading chart...</p>
            </div>
          ) : (
            <canvas id="billDistributionChart" width="400" height="400"></canvas>
          )}
        </div>
      </div>

      {/* Distribution Table */}
      <div className="distribution-table">
        <h3>Detailed Breakdown</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Bill Type</th>
              <th>Amount</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cash Bills</td>
              <td>{formatCurrency(billDistribution.cashBills)}</td>
              <td>{getTotalAmount() > 0 ? ((billDistribution.cashBills / getTotalAmount()) * 100).toFixed(1) : 0}%</td>
            </tr>
            <tr>
              <td>Credit Bills</td>
              <td>{formatCurrency(billDistribution.creditBills)}</td>
              <td>{getTotalAmount() > 0 ? ((billDistribution.creditBills / getTotalAmount()) * 100).toFixed(1) : 0}%</td>
            </tr>
            <tr>
              <td>Credit Notes</td>
              <td>{formatCurrency(billDistribution.creditNotes)}</td>
              <td>{getTotalAmount() > 0 ? ((billDistribution.creditNotes / getTotalAmount()) * 100).toFixed(1) : 0}%</td>
            </tr>
            <tr>
              <td>Debit Notes</td>
              <td>{formatCurrency(billDistribution.debitNotes)}</td>
              <td>{getTotalAmount() > 0 ? ((billDistribution.debitNotes / getTotalAmount()) * 100).toFixed(1) : 0}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BillDistribution;